import { useEffect, useState } from 'react';

import { subscribeToThemeClass } from './themeClass';

/**
 * Resolves theme-aware colors for charts (Recharts).
 *
 * Recharts passes colors as SVG presentation ATTRIBUTES (e.g. stroke="..."),
 * and `var()` is NOT resolved inside plain SVG attributes — only inside CSS
 * properties. So we cannot hand Recharts `hsl(var(--border))` directly; we
 * read the computed CSS variable value at runtime and hand it a concrete
 * `hsl(...)` string, recomputing whenever the theme mode flips.
 */
function readVar(name: string): string {
  if (typeof window === 'undefined') return '';
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value ? `hsl(${value})` : '';
}

/** Data-series ramp (maps to --chart-series-*). A single hue stepped by
 *  lightness/saturation: indigo in light, lavender in dark. `s1` is the most
 *  prominent step, `s5` the most muted — so ordinal charts can read
 *  Strong→Weak and nominal charts stay on-brand. */
export interface ChartSeries {
  s1: string;
  s2: string;
  s3: string;
  s4: string;
  s5: string;
}

export interface ChartColors {
  /** Grid lines / axis lines / hover cursor — maps to --border. */
  grid: string;
  /** Axis tick labels — maps to --muted-foreground. */
  axis: string;
  /** Empty/placeholder fills (empty pie slice, track) — maps to --muted. */
  muted: string;
  /** Theme-aware categorical data-series colors. */
  series: ChartSeries;
}

function compute(): ChartColors {
  return {
    grid: readVar('--border'),
    axis: readVar('--muted-foreground'),
    muted: readVar('--muted'),
    series: {
      s1: readVar('--chart-series-1'),
      s2: readVar('--chart-series-2'),
      s3: readVar('--chart-series-3'),
      s4: readVar('--chart-series-4'),
      s5: readVar('--chart-series-5'),
    },
  };
}

export function useChartColors(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(compute);

  /**
   * Invalidate off the `light`/`dark` class on `<html>`, not off the theme
   * context.
   *
   * The values above are read from `document.documentElement`'s computed
   * style, so the signal that they are stale belongs to the same source of
   * truth. `useTheme().theme.mode` is a second copy of that state, and reading
   * it here cost two things:
   *
   * - **An OS theme flip did not repaint the charts.** With `mode: 'system'`
   *   the mode string never changes, so the effect never re-ran even though
   *   `@ai-tutor/ui`'s `useTheme` had already swapped the class and every CSS
   *   variable underneath it.
   * - **It forced a `ThemeProvider` on every chart consumer**, which made
   *   `MindspaceSection` unmountable in Storybook.
   *
   * Both disappear by watching the class - see `./themeClass` for the full
   * reasoning, which `MindspaceGraph` shares. The app path is unchanged in
   * effect: a mode change still ends in a class swap, one hop later.
   */
  useEffect(() => {
    const sync = () =>
      setColors(prev => {
        const next = compute();
        // The observer fires twice per toggle (`remove` then `add`), and
        // `compute()` is a fresh object every call, so bail on no-op writes
        // rather than re-rendering every chart twice.
        return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
      });

    const unsubscribe = subscribeToThemeClass(sync);
    // The class may have changed between the initial `useState(compute)` and
    // this effect running.
    sync();
    return unsubscribe;
  }, []);

  return colors;
}
